import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  FolderOpen,
  CheckCircle2,
  XCircle,
  CreditCard,
  FileText,
  Send,
  User,
  Users,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusPage = ({ user, settings }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientStatus, setClientStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("code_client");
  const [sortOrder, setSortOrder] = useState("asc");
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterAndSortClients();
  }, [clients, searchTerm, sortField, sortOrder]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        withCredentials: true,
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortClients = () => {
    let result = [...clients];

    // Filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (client) =>
          client.code_client.toLowerCase().includes(search) ||
          client.nom.toLowerCase().includes(search) ||
          client.prenom.toLowerCase().includes(search) ||
          client.ville.toLowerCase().includes(search)
      );
    }

    // Sort
    result.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortField) {
        case "nom":
          valueA = `${a.nom} ${a.prenom}`.toLowerCase();
          valueB = `${b.nom} ${b.prenom}`.toLowerCase();
          break;
        case "prenom":
          valueA = `${a.prenom} ${a.nom}`.toLowerCase();
          valueB = `${b.prenom} ${b.nom}`.toLowerCase();
          break;
        case "ville":
          valueA = a.ville.toLowerCase();
          valueB = b.ville.toLowerCase();
          break;
        case "code_client":
        default:
          valueA = a.code_client;
          valueB = b.code_client;
          break;
      }

      if (sortOrder === "asc") {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });

    setFilteredClients(result);
  };

  const fetchClientStatus = async (client) => {
    setSelectedClient(client);
    setDetailsLoading(true);
    setDialogOpen(true);

    try {
      const response = await axios.get(`${API}/status/${client.client_id}`, {
        withCredentials: true,
      });
      setClientStatus(response.data);
    } catch (error) {
      console.error("Error fetching status:", error);
      toast.error("Erreur lors du chargement du statut");
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sortOrder === "asc" ? (
      <ArrowUp className="h-4 w-4 text-primary" />
    ) : (
      <ArrowDown className="h-4 w-4 text-primary" />
    );
  };

  const StatusBadge = ({ status, label }) => (
    <div className="flex items-center gap-2">
      <span className={`status-indicator ${status ? "green" : "red"}`} />
      <span className={`text-sm font-medium ${status ? "text-green-600" : "text-red-600"}`}>
        {label || (status ? "OUI" : "NON")}
      </span>
    </div>
  );

  const StatusRow = ({ label, status, icon: Icon, detail }) => (
    <div className="flex items-center justify-between p-4 border-b last:border-b-0">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground">{label}</p>
          {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
        </div>
      </div>
      <StatusBadge status={status} />
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="status-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Suivi de Dossier
          </h1>
          <p className="text-muted-foreground mt-1">
            Cliquez sur un client pour voir l'état complet de son dossier
          </p>
        </div>

        {/* Search and Sort Controls */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par code, nom, ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Trier par:</span>
                <Select value={sortField} onValueChange={setSortField}>
                  <SelectTrigger className="w-40" data-testid="sort-field-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="code_client">Code client</SelectItem>
                    <SelectItem value="nom">Nom</SelectItem>
                    <SelectItem value="prenom">Prénom</SelectItem>
                    <SelectItem value="ville">Ville</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                  data-testid="sort-order-button"
                >
                  {sortOrder === "asc" ? (
                    <ArrowUp className="h-4 w-4" />
                  ) : (
                    <ArrowDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clients List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-primary" />
              Liste des clients
            </CardTitle>
            <CardDescription>
              {filteredClients.length} client{filteredClients.length !== 1 ? "s" : ""} 
              {searchTerm && ` trouvé${filteredClients.length !== 1 ? "s" : ""}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {searchTerm ? "Aucun client trouvé" : "Aucun client enregistré"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="clients-status-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pl-6">
                        <button
                          onClick={() => toggleSort("code_client")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Code
                          <SortIcon field="code_client" />
                        </button>
                      </th>
                      <th>
                        <button
                          onClick={() => toggleSort("nom")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Nom
                          <SortIcon field="nom" />
                        </button>
                      </th>
                      <th>
                        <button
                          onClick={() => toggleSort("ville")}
                          className="flex items-center gap-1 hover:text-primary transition-colors"
                        >
                          Ville
                          <SortIcon field="ville" />
                        </button>
                      </th>
                      <th>Situation</th>
                      <th>Conjoint(e)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr
                        key={client.client_id}
                        className="cursor-pointer hover:bg-primary/5 transition-colors"
                        onClick={() => fetchClientStatus(client)}
                        data-testid={`client-row-${client.code_client}`}
                      >
                        <td className="pl-6">
                          <span className="font-mono text-sm font-medium text-primary hover:underline">
                            {client.code_client}
                          </span>
                        </td>
                        <td>
                          <p className="font-medium text-foreground hover:text-primary">
                            {client.prenom} {client.nom}
                          </p>
                          <p className="text-xs text-muted-foreground">{client.email}</p>
                        </td>
                        <td className="text-muted-foreground">{client.ville}</td>
                        <td>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            client.situation_familiale === "Marié(e)" ||
                            client.situation_familiale === "Conjoint(e) de fait"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-muted text-muted-foreground"
                          }`}>
                            {client.situation_familiale}
                          </span>
                        </td>
                        <td>
                          {client.conjoint ? (
                            <span className="text-sm">
                              {client.conjoint.prenom} {client.conjoint.nom}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Details Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-primary" />
                État du dossier - {selectedClient?.code_client}
              </DialogTitle>
            </DialogHeader>

            {detailsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : clientStatus ? (
              <div className="space-y-6">
                {/* Client Info */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Client:</span>
                      <p className="font-medium">
                        {clientStatus.client.prenom} {clientStatus.client.nom}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Situation:</span>
                      <p className="font-medium">{clientStatus.client.situation_familiale}</p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Ville:</span>
                      <p className="font-medium">{clientStatus.client.ville}</p>
                    </div>
                    {clientStatus.client.conjoint && (
                      <div>
                        <span className="text-sm text-muted-foreground">Conjoint(e):</span>
                        <p className="font-medium">
                          {clientStatus.client.conjoint.prenom} {clientStatus.client.conjoint.nom}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financial Status */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    État financier
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-muted/50 text-center">
                      <p className="text-xs text-muted-foreground">Honoraires</p>
                      <p className="font-mono font-medium text-lg">
                        {(clientStatus.payment.honoraires_convenus || 0).toFixed(2)} $
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-green-50 text-center">
                      <p className="text-xs text-green-600">Payé</p>
                      <p className="font-mono font-medium text-lg text-green-700">
                        {clientStatus.payment.total_paid.toFixed(2)} $
                      </p>
                      {clientStatus.payment.payment_date && (
                        <p className="text-xs text-green-600 mt-1">
                          {format(new Date(clientStatus.payment.payment_date), "dd/MM/yyyy", { locale: fr })}
                        </p>
                      )}
                    </div>
                    <div className={`p-3 rounded-lg text-center ${
                      clientStatus.payment.balance > 0 
                        ? "bg-red-50" 
                        : clientStatus.payment.balance < 0 
                          ? "bg-green-50" 
                          : "bg-green-50"
                    }`}>
                      <p className={`text-xs ${
                        clientStatus.payment.balance > 0 
                          ? "text-red-600" 
                          : "text-green-600"
                      }`}>
                        {clientStatus.payment.balance > 0 
                          ? "Reste à payer" 
                          : clientStatus.payment.balance < 0 
                            ? "Trop-perçu"
                            : "Solde"
                        }
                      </p>
                      <p className={`font-mono font-medium text-lg ${
                        clientStatus.payment.balance > 0 
                          ? "text-red-700" 
                          : "text-green-700"
                      }`}>
                        {Math.abs(clientStatus.payment.balance).toFixed(2)} $
                      </p>
                    </div>
                  </div>
                </div>

                {/* Status Details */}
                <div className="divide-y rounded-lg border">
                  <StatusRow
                    label="Paiement"
                    status={clientStatus.payment.complete}
                    icon={CreditCard}
                    detail={
                      clientStatus.payment.complete
                        ? "Honoraires payés en totalité"
                        : `Solde: ${clientStatus.payment.balance.toFixed(2)} $`
                    }
                  />
                  <StatusRow
                    label="Formulaires signés - Requérant principal"
                    status={clientStatus.forms.principal_signed}
                    icon={FileText}
                  />
                  {clientStatus.forms.has_spouse && clientStatus.client.conjoint?.residence !== "Non Résident" && (
                    <StatusRow
                      label="Formulaires signés - Conjoint(e)"
                      status={clientStatus.forms.spouse_signed}
                      icon={FileText}
                    />
                  )}
                  <StatusRow
                    label="TED terminé - Requérant principal"
                    status={clientStatus.transmission.principal_transmitted}
                    icon={Send}
                    detail={
                      clientStatus.transmission.principal_date
                        ? `Transmis le ${format(new Date(clientStatus.transmission.principal_date), "dd/MM/yyyy", { locale: fr })}`
                        : undefined
                    }
                  />
                  {clientStatus.forms.has_spouse && clientStatus.client.conjoint?.residence !== "Non Résident" && (
                    <StatusRow
                      label="TED terminé - Conjoint(e)"
                      status={clientStatus.transmission.spouse_transmitted}
                      icon={Send}
                      detail={
                        clientStatus.transmission.spouse_date
                          ? `Transmis le ${format(new Date(clientStatus.transmission.spouse_date), "dd/MM/yyyy", { locale: fr })}`
                          : undefined
                      }
                    />
                  )}
                </div>

                {/* Overall Status */}
                <div>
                  {(() => {
                    const isSpouseNonResident = clientStatus.client.conjoint?.residence === "Non Résident";
                    const spouseSignedOrNA = !clientStatus.forms.has_spouse || isSpouseNonResident || clientStatus.forms.spouse_signed;
                    const spouseTransmittedOrNA = !clientStatus.forms.has_spouse || isSpouseNonResident || clientStatus.transmission.spouse_transmitted;
                    
                    const isComplete = clientStatus.payment.complete &&
                      clientStatus.forms.principal_signed &&
                      spouseSignedOrNA &&
                      clientStatus.transmission.principal_transmitted &&
                      spouseTransmittedOrNA;
                    
                    return isComplete ? (
                    <div className="p-4 rounded-lg bg-green-50 border border-green-200 flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-700">Dossier complété</p>
                        <p className="text-sm text-green-600">
                          Toutes les étapes ont été complétées avec succès
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200 flex items-center gap-3">
                      <XCircle className="h-6 w-6 text-yellow-600" />
                      <div>
                        <p className="font-medium text-yellow-700">Dossier en cours</p>
                        <p className="text-sm text-yellow-600">
                          Certaines étapes restent à compléter
                        </p>
                      </div>
                    </div>
                  )
                  })()}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StatusPage;
